<template>
  <v-navigation-drawer
    class="main-nav-drawer"
    :width="$vuetify.display.xs ? $vuetify.display.width : 300"
    :elevation="0"
    :border="0"
    :rail="rail"
    :rail-width="54"
    :disable-resize-watcher="true"
    :model-value="$vuetify.display.xs ? drawerStore.drawerMobileOpen : true"
    :permanent="!$vuetify.display.xs && !$vuetify.display.sm"
    :temporary="$vuetify.display.xs || $vuetify.display.sm"
    :scrim="false"
    :touchless="true"
  >
    <v-list-item v-if="!$vuetify.display.xs" class="main-nav-drawer-header-wrapper pa-0 mt-2">
      <div class="main-nav-drawer-header d-flex">
        <v-icon class="main-nav-drawer-trigger-icon ml-4" @click.stop="toggleDrawer">
          {{ `fa-light fa-bars${rail ? "" : "-staggered"}` }}
        </v-icon>
        <div class="ml-6 soma-logo align-center justify-center">
          <span class="d-flex align-center">
            <v-icon icon="custom:somaLogo" :size="40" />
            <p>Soma</p>
          </span>
        </div>
      </div>
    </v-list-item>

    <v-list
      class="main-nav-drawer-menu mx-1 mt-2"
      :mandatory="true"
      :selected="mainMenuStore.selectedItemValues"
      :opened="mainMenuStore.selectedItemValues"
    >
      <template v-for="(item, i) in mainMenuStore.items">
        <nuxt-link v-if="isVisible(item) && item.categories.length === 0" :key="i" :to="itemUrl(item)">
          <div
            class="drawer-menu-item d-flex align-center py-2 my-1"
            :class="{ selected: mainMenuStore.isSelected(item.value), disabled: isLocked(item) }"
          >
            <v-icon
              :icon="`fa-${mainMenuStore.isSelected(item.value) ? 'solid' : 'light'} ${
                isLocked(item) ? 'fa-lock' : item.icon
              }`"
              :size="20"
            />
            <div class="item-title" :aria-label="item.title">
              {{ item.title }}
            </div>
          </div>
        </nuxt-link>

        <v-list-group
          v-if="isVisible(item) && item.categories.length !== 0"
          :key="item.value"
          :value="rail ? null : item.value"
          class="drawer-menu-item-group"
        >
          <template #activator="{ isOpen, props }">
            <div v-bind="isLocked(item) ? {} : props" @click="isLocked(item) ? showUpgradeNotice() : undefined">
              <div
                class="d-flex align-center justify-space-between py-2 pl-3 activator-item"
                :class="{ selected: isOpen, disabled: isLocked(item) }"
                @click="openDrawer()"
              >
                <v-icon :icon="`fa-light ${isLocked(item) ? 'fa-lock' : item.icon}`" :size="20" />
                <div class="item-title d-flex align-center justify-space-between">
                  <span :aria-label="item.title" :style="listGroupActivatorTitleWidth">
                    {{ item.title }}
                  </span>
                  <v-icon :size="16">fa-regular fa-angle-{{ isOpen ? "up" : "down" }}</v-icon>
                </div>
              </div>
            </div>
          </template>

          <template v-for="category in item.categories">
            <nuxt-link v-if="isVisible(category)" :key="category.value" :to="itemUrl(category)">
              <div
                class="drawer-menu-item sub-menu d-flex align-center py-2 pl-3 my-1"
                :class="{ selected: mainMenuStore.isSelected(category.value), disabled: isLocked(item) }"
              >
                <v-icon
                  :icon="`fa-${mainMenuStore.isSelected(category.value) ? 'solid' : 'light'} ${category.icon}`"
                  :size="20"
                />
                <div class="item-title" :aria-label="category.title">
                  {{ category.title }}
                </div>
              </div>
            </nuxt-link>
          </template>
        </v-list-group>
      </template>
    </v-list>

    <template v-if="$vuetify.display.xs && drawerStore.drawerMobileOpen" #append>
      <div class="pa-2">
        <search-bar :inside-drawer="true" />
      </div>
    </template>
    <layout-upgrade-notice-modal :show-dialog="upgradeNoticeVisible" @close-upgrade-notice="closeUpgradeNotice" />
  </v-navigation-drawer>
</template>

<script lang="ts">
import { useDrawerStore } from "~/stores/drawerStore"
import { useMainMenuStore } from "~/stores/mainMenuStore"
import usePlaylistsStore from "~/stores/playlistsStore"
import { useUserStore } from "~/stores/userStore"
import { MenuItem } from "~/types/MenuItem"

export default {
  setup() {
    const playlistStore = usePlaylistsStore()
    const drawerStore = useDrawerStore()
    const mainMenuStore = useMainMenuStore()
    const userStore = useUserStore()
    const config = useRuntimeConfig()
    return { drawerStore, mainMenuStore, userStore, playlistStore, config }
  },

  computed: {
    rail() {
      return !this.drawerStore.drawerDesktopOpen && !this.$vuetify.display.xs
    },

    borderClass() {
      return this.rail ? "list-item-closed-border" : "list-item-open-border"
    },

    listGroupActivatorTitleWidth() {
      // Since menu width is fixed, we can adjust title width accordingly for submenu activator icon to be in the right place
      return { width: this.$vuetify.display.xs ? `${this.$vuetify.display.width - 100}px` : "210px" }
    },

    currentUser() {
      return this.userStore?.current
    },

    isStudent() {
      return ["mykoob", "eklase"].includes(this.currentUser?.provider || "") && this.userStore.isStudent
    },

    hasPlaylists() {
      return this.playlistStore.playlists?.length > 0
    },

    upgradeNoticeVisible() {
      return this.userStore.upgradeNoticeVisible || this.userStore.upgradeNoticeWithRedirectHome
    },
  },

  watch: {
    $route(to, _from) {
      this.mainMenuStore.selectActiveItemValue(to)
    },
  },

  mounted() {
    this.mainMenuStore.selectActiveItemValue(this.$route)
  },

  methods: {
    processOldUrl(item: MenuItem, route: string) {
      if (item.value === "playlists") {
        const firstPlaylistName = this.playlistStore.playlists[0]?.slug
        route = firstPlaylistName ? route.replace(":slug", firstPlaylistName) : route.replace("/:slug", "s")
      }
      return route
    },

    itemUrl(item: MenuItem) {
      if (item.oldUrl !== undefined) {
        const path = this.config.public.oldFrontendRoot + item.oldUrl
        return this.processOldUrl(item, path)
      } else {
        return item.url
      }
    },

    toggleDrawer() {
      this.drawerStore.closeContentMenu()
      this.drawerStore.toggleDesktopDrawer()
    },

    openDrawer() {
      this.drawerStore.closeContentMenu()
      this.drawerStore.openDesktopDrawer()
    },

    isVisible(item: MenuItem) {
      if (item.alwaysVisible) {
        return true
      } else if (item.value === "playlists") {
        return this.hasPlaylists
      } else if (item.value === "methodical-supplies") {
        return this.currentUser?.plan !== "plus"
      } else if (item.progressometer) {
        return this.currentUser?.user_type !== "teacher"
      } else {
        return !this.isLocked(item)
      }
    },

    isLocked(item: MenuItem) {
      if (item.public) {
        return false
      }

      if (item.hasActivatedProducts) {
        return !this.currentUser?.has_activated_products
      }

      if (item.activeInspire) {
        return !this.currentUser?.has_active_inspire
      }

      if (item.value === "test-works") {
        return !this.currentUser?.has_test_works
      }

      if (item.coursesAllowed) {
        return !this.currentUser?.courses_allowed
      }

      if (item.coursesUserActiveGroup && !this.currentUser?.courses_allowed) {
        return !this.currentUser?.courses_user_active_group
      }

      let result = false

      // Plan chack moved to per product
      if (item.plans && item.plans.length > 0 && this.currentUser?.plan) {
        result =
          !item.plans.includes(this.currentUser?.plan) && !(this.currentUser?.whitelisted || this.currentUser?.admin)
      }

      if (item.studentWithLessons && item.teacherWithLessons) {
        result = result || !(this.isStudent || this.userStore.isTeacherWithLessons)
      } else if (item.studentWithLessons) {
        result = result || !this.isStudent
      } else if (item.teacherWithLessons) {
        result = result || !this.userStore.isTeacherWithLessons
      }

      return result
    },

    closeUpgradeNotice() {
      if (this.userStore.upgradeNoticeWithRedirectHome) {
        this.userStore.upgradeNoticeWithRedirectHome = false
        this.$router.push("/")
      } else {
        this.userStore.upgradeNoticeVisible = false
      }
    },

    showUpgradeNotice() {
      this.userStore.upgradeNoticeVisible = true
    },
  },
}
</script>

<style lang="sass">
@use "~/assets/css/vuetify/settings" as *

$list-item-open-border-radius: 36px
$list-item-closed-border-radius: 8px
$selected-background-color: #f9d805
$default-background-color: #fff1ae

@media #{map-get($display-breakpoints, 'sm-and-up')}
  .drawer-menu-item, .activator-item
    &:hover
      background: rgba(0, 0, 0, 0.08)

.list-item-open-border
  border-radius: $list-item-open-border-radius

.list-item-closed-border
  border-radius: $list-item-closed-border-radius

.v-list-item--active > .v-list-item__overlay
  opacity: 0 !important

.v-list-group__items
  --indent-padding: 0px
  padding-left: 50px

.main-nav-drawer
  background-color: $default-background-color

.main-nav-drawer-menu
  .drawer-menu-item-group
    .activator-item
      cursor: pointer
      border-radius: 24px
      overflow: hidden
      height: 44px
      &.selected
        background: rgba(0, 0, 0, 0.08)
      .item-title
        position: absolute
        left: 49px
  a
    color: black
    text-decoration: none
    .drawer-menu-item
      padding-left: 13px
      border-radius: 24px
      overflow: hidden
      height: 44px
      &.selected
        background: $selected-background-color
      .item-title
        width: 230px
        position: absolute
        left: 49px
      &.sub-menu > .item-title
        position: relative
        left: 23px

  .v-list-item--active
    background-color: $selected-background-color
    .v-list-item-title
      font-weight: bold

  .disabled
    color: #505050

.main-nav-drawer-header-logo
  left: 60px
  top: 4px
  position: absolute
  display: flex

.main-nav-drawer-trigger-icon
  margin-top: 10px
  font-size: 20px
  line-height: 100%
</style>
